import type { FC } from 'react';
import styles from './SendResults.module.scss';

interface SendCellValueProps {
  value: string;
}

const LEADING = /^\s+/;
const TRAILING = /\s+$/;

const marker = (char: string): string => {
  if (char === ' ') {
    return '␣';
  }
  return char === '\t' ? '⇥' : '·';
};

const describe = (whitespace: string, where: string): string => {
  const count = whitespace.length;
  const noun = /^ +$/.test(whitespace) ? 'space' : 'whitespace character';
  return `${count} ${where} ${noun}${count === 1 ? '' : 's'}`;
};

/**
 * A value as it appears in the customer's file, with its whitespace kept and any leading or trailing
 * whitespace marked.
 *
 * Controlled terminology is compared exactly, so " M" fails where "M" passes. HTML collapses
 * whitespace, so drawn plainly the failing value would look identical to the term it should be.
 */
const SendCellValue: FC<SendCellValueProps> = ({ value }) => {
  const leading = LEADING.exec(value)?.[0] ?? '';
  const rest = value.slice(leading.length);
  const trailing = TRAILING.exec(rest)?.[0] ?? '';
  const core = rest.slice(0, rest.length - trailing.length);

  if (leading.length === 0 && trailing.length === 0) {
    return <span className={styles.cellValue}>{value}</span>;
  }

  const description = [
    leading.length > 0 ? describe(leading, 'leading') : null,
    trailing.length > 0 ? describe(trailing, 'trailing') : null,
  ]
    .filter((part): part is string => part !== null)
    .join(' and ');

  return (
    <span className={styles.cellValue} title={description} data-test-element="cell-value">
      {leading.length > 0 && (
        <span className={styles.whitespaceMarker} aria-hidden="true" data-test-element="leading-whitespace">
          {[...leading].map(marker).join('')}
        </span>
      )}
      {core}
      {trailing.length > 0 && (
        <span className={styles.whitespaceMarker} aria-hidden="true" data-test-element="trailing-whitespace">
          {[...trailing].map(marker).join('')}
        </span>
      )}
      <span className="clip"> ({description})</span>
    </span>
  );
};

export default SendCellValue;
